import { useEffect, useRef, useCallback } from "react";

interface InfiniteScrollResult {
  tableBodyRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Handles infinite scroll detection and scroll event listener
 * Automatically loads more data when user scrolls near the bottom
 *
 * Performance optimizations:
 * - Uses useRef to track if listener is attached to avoid re-attachment on every render
 * - Memoizes scroll handler to prevent function recreation
 * - Only re-attaches listener when loadMore reference changes (not on hasNextPage/isFetchingNextPage changes)
 * - Includes debug logging for performance monitoring
 * 
 * Scroll Threshold:
 * - Triggers when user is within 50px of the bottom (allows scrolling to actual bottom before loading)
 * - Prevents multiple simultaneous requests with hasNextPage and isFetchingNextPage checks
 */
export function useSimpleTableInfiniteScroll(
  loadMore?: () => void,
  hasNextPage?: boolean,
  isFetchingNextPage?: boolean
): InfiniteScrollResult {
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const listenerAttachedRef = useRef(false);
  const lastLoadMoreRef = useRef(loadMore);
  const attachCountRef = useRef(0);
  const scrollEventCountRef = useRef(0);
  const lastLoadTimeRef = useRef(0);

  // Memoize scroll handler to prevent recreation on every render
  const handleScroll = useCallback(() => {
    const element = tableBodyRef.current;
    if (!element) return;

    scrollEventCountRef.current++;

    // Check if user has scrolled within 50px of the bottom
    // This allows users to scroll to the actual bottom before triggering the load
    const isNearBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 50;

    if (isNearBottom && hasNextPage && !isFetchingNextPage && lastLoadMoreRef.current) {
      // Add debounce to prevent multiple rapid calls
      const now = Date.now();
      if (now - lastLoadTimeRef.current > 500) {
        lastLoadTimeRef.current = now;
        lastLoadMoreRef.current();
        
        if (process.env.NODE_ENV === "development") {
          console.debug("[InfiniteScroll] Load triggered", {
            scrollTop: element.scrollTop,
            scrollHeight: element.scrollHeight,
            clientHeight: element.clientHeight,
            distanceFromBottom: element.scrollHeight - element.scrollTop - element.clientHeight,
          });
        }
      }
    }
  }, [hasNextPage, isFetchingNextPage]);

  // Update the ref whenever loadMore changes
  useEffect(() => {
    lastLoadMoreRef.current = loadMore;
  }, [loadMore]);

  // Add scroll event listener for infinite scroll
  // Only re-attach when loadMore reference changes, not on every render
  useEffect(() => {
    const element = tableBodyRef.current;
    if (!element || !loadMore) {
      // Clean up listener if loadMore is removed
      if (listenerAttachedRef.current && element) {
        element.removeEventListener("scroll", handleScroll);
        listenerAttachedRef.current = false;
        if (process.env.NODE_ENV === "development") {
          console.debug("[InfiniteScroll] Scroll listener detached", {
            attachCount: attachCountRef.current,
            scrollEventCount: scrollEventCountRef.current,
          });
        }
      }
      return;
    }

    // Only attach listener once per loadMore reference
    if (!listenerAttachedRef.current) {
      element.addEventListener("scroll", handleScroll, { passive: true });
      listenerAttachedRef.current = true;
      attachCountRef.current++;
      scrollEventCountRef.current = 0;
      lastLoadTimeRef.current = 0;
      if (process.env.NODE_ENV === "development") {
        console.debug("[InfiniteScroll] Scroll listener attached", {
          attachCount: attachCountRef.current,
          elementHeight: element.clientHeight,
          scrollHeight: element.scrollHeight,
        });
      }
    }

    return () => {
      element.removeEventListener("scroll", handleScroll);
      listenerAttachedRef.current = false;
      if (process.env.NODE_ENV === "development") {
        console.debug("[InfiniteScroll] Scroll listener cleanup", {
          attachCount: attachCountRef.current,
          scrollEventCount: scrollEventCountRef.current,
        });
      }
    };
  }, [loadMore, handleScroll]);

  return {
    tableBodyRef,
  };
}
